import { useContext, useState } from "react";
import {
    Button,
    Card,
    Col,
    Input,
    message,
    Modal,
    Row,
    Select,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {
    ApiOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    CopyOutlined,
    DesktopOutlined,
    HistoryOutlined,
    ReloadOutlined,
    SearchOutlined,
    UserOutlined,
    WarningOutlined,
} from "@ant-design/icons";

import useAccessLogs from "./useAccessLogs";
import PageHeader from "../../components/PageHeader";
import ActiveFiltersBar from "../../components/ActiveFiltersBar";
import TableCard from "../../components/TableCard";
import TableDicaHint from "../../components/TableDicaHint";
import TablePagination from "../../components/TablePagination";
import { AsyncSelect } from "../../components/AsyncSelect";
import DatePicker from "../../components/DatePicker";
import PanelUsersService from "../../services/http/services/PanelUsersService";
import RolesService from "../../services/http/services/RolesService";
import CitiesService from "../../services/http/services/CitiesService";
import { ThemeContext } from "../../contexts/ThemeContext";
import {
    formatDateStringToDateAndTime,
    poeValorComPontosEmMil,
} from "../../utils/Conversores";

const { Text } = Typography;

export default function AccessLogs() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const {
        columns,
        tableData,
        isLoading,
        page,
        setPage,
        perPage,
        setPerPage,
        total,
        selectedLog,
        isModalVisible,
        handleCloseModal,
        handleOpenModal,
        filters,
        setFilters,
        dateRange,
        setDateRange,
        activeFilterItems,
        handleClearAllFilters,
        loadLogs,
        getMethodTagColor,
        getStatusTagColor,
    } = useAccessLogs();

    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    const handleCopyToClipboard = (text: string, keyName: string, label: string) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedKey(keyName);
        message.success(`${label} copiado para a área de transferência!`);
        setTimeout(() => setCopiedKey(null), 2000);
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <PageHeader
                icon={<HistoryOutlined />}
                iconBg="linear-gradient(135deg, #0284c7 0%, #0369a1 100%)"
                title="Logs de Acesso e Auditoria"
                tag={<Tag color="blue">{poeValorComPontosEmMil(total)} registros</Tag>}
                subtitle="Rastreabilidade, monitoramento de requisições e histórico de atividades no painel administrativo"
                actions={
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => loadLogs()}
                        loading={isLoading}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontWeight: 600,
                            height: 38,
                            borderRadius: 8,
                        }}
                    >
                        Atualizar
                    </Button>
                }
            />

            <div
                style={{
                    background: isDark ? "#141414" : "#ffffff",
                    border: `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                    borderRadius: 10,
                    padding: "14px 16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                }}
            >
                <Row gutter={[12, 10]} align="bottom">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                                Pesquisar
                            </span>
                            <Input
                                placeholder="Buscar por URL, IP, UserAgent..."
                                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                                value={filters.search}
                                onChange={(e) => {
                                    setFilters((old) => ({ ...old, search: e.target.value }));
                                    setPage(1);
                                }}
                                allowClear
                                style={{ borderRadius: 8, height: 36 }}
                            />
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={5}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                                Usuário
                            </span>
                            <AsyncSelect
                                fetchOptions={async () => {
                                    const controller = new AbortController();
                                    return PanelUsersService.listPanelUsers(controller.signal);
                                }}
                                labelKey="name"
                                valueKey="id"
                                placeholder="Todos os usuários"
                                value={filters.panelUserId}
                                onChange={(value) => {
                                    setFilters((old) => ({
                                        ...old,
                                        panelUserId: value || undefined,
                                    }));
                                    setPage(1);
                                }}
                            />
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={5}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                                Município
                            </span>
                            <AsyncSelect
                                fetchOptions={async () => {
                                    const controller = new AbortController();
                                    return CitiesService.listCities(controller.signal);
                                }}
                                labelKey="city"
                                valueKey="id"
                                placeholder="Todos os municípios"
                                value={filters.countyId}
                                onChange={(value) => {
                                    setFilters((old) => ({
                                        ...old,
                                        countyId: value || undefined,
                                    }));
                                    setPage(1);
                                }}
                            />
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={4}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                                Perfil
                            </span>
                            <AsyncSelect
                                fetchOptions={async () => {
                                    const controller = new AbortController();
                                    return RolesService.listRoles(controller.signal);
                                }}
                                labelKey="name"
                                valueKey="id"
                                placeholder="Todos os perfis"
                                value={filters.roleId}
                                onChange={(value) => {
                                    setFilters((old) => ({
                                        ...old,
                                        roleId: value || undefined,
                                    }));
                                    setPage(1);
                                }}
                            />
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={4}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                                Método HTTP
                            </span>
                            <Select
                                allowClear
                                placeholder="Todos"
                                value={filters.method}
                                onChange={(value) => {
                                    setFilters((old) => ({ ...old, method: value || undefined }));
                                    setPage(1);
                                }}
                                style={{ width: "100%", borderRadius: 8 }}
                                options={[
                                    { label: "GET", value: "GET" },
                                    { label: "POST", value: "POST" },
                                    { label: "PUT", value: "PUT" },
                                    { label: "PATCH", value: "PATCH" },
                                    { label: "DELETE", value: "DELETE" },
                                ]}
                            />
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={4}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                                Status
                            </span>
                            <Select
                                allowClear
                                placeholder="Todos"
                                value={filters.success}
                                onChange={(value) => {
                                    setFilters((old) => ({ ...old, success: value }));
                                    setPage(1);
                                }}
                                style={{ width: "100%", borderRadius: 8 }}
                                options={[
                                    { label: "Apenas Sucesso", value: true },
                                    { label: "Apenas Falhas / Erros", value: false },
                                ]}
                            />
                        </div>
                    </Col>

                    <Col xs={24} sm={24} md={12} lg={8}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                                Período
                            </span>
                            <DatePicker.RangePicker
                                style={{ width: "100%", borderRadius: 8 }}
                                placeholder={["Data inicial", "Data final"]}
                                format="DD/MM/YYYY"
                                value={dateRange as any}
                                onChange={(dates: any) => {
                                    setDateRange(dates as any);
                                    setFilters((old) => ({
                                        ...old,
                                        startDate: dates?.[0] || undefined,
                                        endDate: dates?.[1] || undefined,
                                    }));
                                    setPage(1);
                                }}
                            />
                        </div>
                    </Col>
                </Row>
            </div>

            <ActiveFiltersBar
                filters={activeFilterItems}
                onClearAll={handleClearAllFilters}
            />

            <TableDicaHint text="Clique em qualquer linha da tabela para inspecionar os detalhes técnicos completos da requisição, cabeçalhos, dispositivo e diagnósticos." />

            <TablePagination
                current={page}
                pageSize={perPage}
                total={total}
                itemName="logs"
                pageSizeOptions={["10", "20", "50", "100"]}
                onChange={(p, size) => {
                    setPage(p);
                    setPerPage(size);
                }}
            />

            <TableCard>
                <Table
                    columns={columns as any}
                    dataSource={tableData}
                    rowKey="key"
                    loading={isLoading}
                    pagination={false}
                    className={`modern-data-table ${isDark ? "modern-data-table-dark" : ""}`}
                    scroll={{ x: 1000 }}
                    onRow={(record) => ({
                        onClick: () => handleOpenModal(record),
                        style: { cursor: "pointer" },
                    })}
                />
            </TableCard>
            <TablePagination
                current={page}
                pageSize={perPage}
                total={total}
                itemName="logs"
                pageSizeOptions={["10", "20", "50", "100"]}
                onChange={(p, size) => {
                    setPage(p);
                    setPerPage(size);
                }}
            />

            <Modal
                open={isModalVisible}
                onCancel={handleCloseModal}
                footer={
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button type="primary" onClick={handleCloseModal} style={{ borderRadius: 8, fontWeight: 600 }}>
                            Fechar
                        </Button>
                    </div>
                }
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 16,
                            }}
                        >
                            <HistoryOutlined />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ fontSize: 16, fontWeight: 700 }}>Detalhes do Log #{selectedLog?.id}</span>
                            <span style={{ fontSize: 12, fontWeight: 400, color: isDark ? "#8c8c8c" : "#64748b" }}>
                                Registrado em {selectedLog?.created_at ? formatDateStringToDateAndTime(selectedLog.created_at) : "--"}
                            </span>
                        </div>
                    </div>
                }
                width={740}
                centered
                destroyOnClose
            >
                {selectedLog && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 12 }}>
                        <div
                            style={{
                                background: isDark ? "#1f1f1f" : "#f8fafc",
                                border: `1px solid ${isDark ? "#303030" : "#e2e8f0"}`,
                                borderRadius: 8,
                                padding: "12px 16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 10,
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Tag
                                    color={getMethodTagColor(selectedLog.method)}
                                    style={{ fontWeight: 700, fontSize: 12, padding: "2px 8px", borderRadius: 4 }}
                                >
                                    {selectedLog.method}
                                </Tag>
                                <Tag
                                    color={getStatusTagColor(selectedLog.status_code)}
                                    style={{ fontWeight: 700, fontSize: 12, padding: "2px 8px", borderRadius: 4 }}
                                >
                                    Status {selectedLog.status_code || "--"}
                                </Tag>
                                <Tag
                                    color={selectedLog.success ? "green" : "red"}
                                    icon={selectedLog.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                    style={{ fontWeight: 600, fontSize: 12, padding: "2px 8px", borderRadius: 4 }}
                                >
                                    {selectedLog.success ? "Sucesso" : "Falha"}
                                </Tag>
                            </div>

                            {selectedLog.duration_ms !== null && selectedLog.duration_ms !== undefined && (
                                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12.5 }}>
                                    <ClockCircleOutlined style={{ color: "#94a3b8" }} />
                                    <Text type="secondary">Duração:</Text>
                                    <Text strong>{selectedLog.duration_ms} ms</Text>
                                </div>
                            )}
                        </div>

                        {(!selectedLog.success || selectedLog.error_message) && (
                            <div
                                style={{
                                    background: isDark ? "#2a1215" : "#fff1f0",
                                    border: `1px solid ${isDark ? "#5c1d24" : "#ffa39e"}`,
                                    borderRadius: 8,
                                    padding: "12px 16px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 6,
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#cf1322", fontWeight: 600, fontSize: 13 }}>
                                    <WarningOutlined />
                                    <span>Mensagem de Erro / Diagnóstico</span>
                                </div>
                                <div
                                    style={{
                                        fontFamily: "SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                        fontSize: 12,
                                        color: isDark ? "#ffa39e" : "#cf1322",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {selectedLog.error_message || "A requisição retornou com falha, porém nenhuma mensagem de erro adicional foi registrada."}
                                </div>
                            </div>
                        )}

                        <Card
                            size="small"
                            title={
                                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600 }}>
                                    <UserOutlined style={{ color: "#1677ff" }} />
                                    <span>Identificação do Usuário</span>
                                </div>
                            }
                            style={{ borderRadius: 8, border: `1px solid ${isDark ? "#303030" : "#e2e8f0"}` }}
                        >
                            <Row gutter={[16, 12]}>
                                <Col xs={24} sm={12}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <Text type="secondary" style={{ fontSize: 11.5 }}>Nome do Usuário</Text>
                                        <Text strong style={{ fontSize: 13 }}>{selectedLog.panel_user_name || "Sistema / Não identificado"}</Text>
                                    </div>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <Text type="secondary" style={{ fontSize: 11.5 }}>Perfil de Acesso</Text>
                                        <Text strong style={{ fontSize: 13 }}>{selectedLog.role_name || "--"}</Text>
                                    </div>
                                </Col>
                            </Row>
                        </Card>

                        <Card
                            size="small"
                            title={
                                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600 }}>
                                    <ApiOutlined style={{ color: "#0284c7" }} />
                                    <span>Requisição HTTP</span>
                                </div>
                            }
                            style={{ borderRadius: 8, border: `1px solid ${isDark ? "#303030" : "#e2e8f0"}` }}
                        >
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                <div>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                                        <Text type="secondary" style={{ fontSize: 11.5 }}>URL do Endpoint</Text>
                                        {selectedLog.url && (
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<CopyOutlined />}
                                                onClick={() => handleCopyToClipboard(selectedLog.url || "", "url", "URL")}
                                                style={{ fontSize: 11, height: 22, padding: "0 6px" }}
                                            >
                                                {copiedKey === "url" ? "Copiado!" : "Copiar URL"}
                                            </Button>
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            background: isDark ? "#141414" : "#f1f5f9",
                                            border: `1px solid ${isDark ? "#262626" : "#cbd5e1"}`,
                                            borderRadius: 6,
                                            padding: "8px 12px",
                                            fontFamily: "SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                            fontSize: 12,
                                            color: isDark ? "#93c5fd" : "#0284c7",
                                            wordBreak: "break-all",
                                        }}
                                    >
                                        {selectedLog.url || "--"}
                                    </div>
                                </div>

                                <Row gutter={[16, 12]}>
                                    <Col xs={24} sm={12}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                            <Text type="secondary" style={{ fontSize: 11.5 }}>Módulo do Sistema</Text>
                                            <Text strong style={{ fontSize: 13 }}>
                                                {selectedLog.module ? selectedLog.module.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "--"}
                                            </Text>
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                            <Text type="secondary" style={{ fontSize: 11.5 }}>Header Origin</Text>
                                            <Text strong style={{ fontSize: 12.5, wordBreak: "break-all" }}>{selectedLog.origin_header || "--"}</Text>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </Card>

                        <Card
                            size="small"
                            title={
                                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600 }}>
                                    <DesktopOutlined style={{ color: "#8b5cf6" }} />
                                    <span>Conexão & Dispositivo</span>
                                </div>
                            }
                            style={{ borderRadius: 8, border: `1px solid ${isDark ? "#303030" : "#e2e8f0"}` }}
                        >
                            <Row gutter={[16, 12]}>
                                <Col xs={24} sm={12} md={6}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <Text type="secondary" style={{ fontSize: 11.5 }}>Endereço IP</Text>
                                            {selectedLog.ip && (
                                                <Tooltip title="Copiar IP">
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<CopyOutlined />}
                                                        onClick={() => handleCopyToClipboard(selectedLog.ip || "", "ip", "IP")}
                                                        style={{ fontSize: 10, height: 18, width: 18, padding: 0 }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                        <Text strong style={{ fontSize: 13, fontFamily: "monospace" }}>{selectedLog.ip || "--"}</Text>
                                    </div>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <Text type="secondary" style={{ fontSize: 11.5 }}>Navegador</Text>
                                        <Text strong style={{ fontSize: 13 }}>{selectedLog.browser || "--"}</Text>
                                    </div>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <Text type="secondary" style={{ fontSize: 11.5 }}>Sistema Operacional</Text>
                                        <Text strong style={{ fontSize: 13 }}>{selectedLog.os || "--"}</Text>
                                    </div>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <Text type="secondary" style={{ fontSize: 11.5 }}>Dispositivo</Text>
                                        <Text strong style={{ fontSize: 13 }}>{selectedLog.device || "--"}</Text>
                                    </div>
                                </Col>
                                {selectedLog.user_agent && (
                                    <Col xs={24}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 4 }}>
                                            <Text type="secondary" style={{ fontSize: 11.5 }}>User Agent Completo</Text>
                                            <div
                                                style={{
                                                    background: isDark ? "#141414" : "#f8fafc",
                                                    border: `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                                                    borderRadius: 6,
                                                    padding: "6px 10px",
                                                    fontSize: 11.5,
                                                    fontFamily: "monospace",
                                                    color: isDark ? "#a6a6a6" : "#64748b",
                                                    wordBreak: "break-all",
                                                }}
                                            >
                                                {selectedLog.user_agent}
                                            </div>
                                        </div>
                                    </Col>
                                )}
                            </Row>
                        </Card>
                    </div>
                )}
            </Modal>
        </div>
    );
}